import React, { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { DEFAULT_PAGE_SIZE } from "features/api";
import {
  SecretAuditEvent,
  useListSecretAuditQuery,
} from "features/settings/secrets";

const Name = ({ children }: { children: string }) => (
  <Box component="em" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
    {children}
  </Box>
);

const describeChange = (event: SecretAuditEvent): React.ReactNode => {
  switch (event.event) {
    case "secret_created":
      return (
        <>
          created <Name>{event.secret_name}</Name>
        </>
      );
    case "value_changed":
      return (
        <>
          changed the value of <Name>{event.secret_name}</Name>
        </>
      );
    case "description_changed":
      return (
        <>
          changed the description of <Name>{event.secret_name}</Name>
        </>
      );
    case "integration_changed":
      return (
        <>
          set {event.integration_label}:{" "}
          <Name>{event.old_secret_name || "none"}</Name> →{" "}
          <Name>{event.new_secret_name || "none"}</Name>
        </>
      );
    default:
      return event.event_label;
  }
};

/** Who changed which secret or integration field, newest first, paged. */
const SecretAuditLog = () => {
  const [page, setPage] = useState(0);
  // The API pages from 1; MUI's TablePagination from 0.
  const { data, isLoading, isError } = useListSecretAuditQuery(page + 1);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress aria-label="Loading the audit log" />
        </Box>
      );
    }
    if (isError || !data) {
      return <Alert severity="error">Failed to load the audit log.</Alert>;
    }
    if (data.count === 0) {
      return (
        <Typography variant="body2" color="text.secondary" py={2}>
          No changes yet.
        </Typography>
      );
    }
    return (
      <>
        {/* Like the secrets table, a narrow screen scrolls here, not the page. */}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            aria-label="Secrets audit log"
            sx={{ minWidth: 560 }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>When</TableCell>
                <TableCell>Change</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.map((event) => (
                <TableRow key={event.id}>
                  <TableCell sx={{ whiteSpace: "nowrap", width: "1%" }}>
                    <time dateTime={event.created_on}>
                      {new Date(event.created_on).toLocaleString()}
                    </time>
                  </TableCell>
                  <TableCell>
                    {event.user_display} {describeChange(event)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.count}
          page={page}
          onPageChange={(_event, next) => setPage(next)}
          rowsPerPage={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={[]}
        />
      </>
    );
  };

  return (
    <Box mt={4}>
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Audit log
      </Typography>
      {renderBody()}
    </Box>
  );
};

export default SecretAuditLog;
